<script setup lang="ts">
import { AccountIcon, ActionButton, Dialog, Select, StatusTag, TextField } from '@taler/ui'
import { CurrencyCode } from '@taler/domain'
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  ACCOUNT_CLASSES,
  ACCOUNT_COLOR_PRESETS,
  ACCOUNT_ICONS,
  ACCOUNT_KINDS,
  DEFAULT_ACCOUNT_COLOR,
  DEFAULT_ACCOUNT_ICON,
  FRIENDLY_CREATE_ERRORS,
  LEGAL_TYPES,
  currencyRule,
  generateAccountCode,
  type AccountKind,
  type CreateAccountError,
  type CreateAccountInput,
} from '../internal/account-form.ts'
import { classRootOf, parentChoices, type ParentOption } from '../internal/account-tree.ts'

const props = defineProps<{
  open: boolean
  defaultParentId: string | undefined
  parentOptions: readonly ParentOption[]
  isSubmitting: boolean
  error: CreateAccountError | undefined
}>()

const emit = defineEmits<{ submit: [input: CreateAccountInput]; close: [] }>()

const { t, locale } = useI18n()

const firstFieldRef = useTemplateRef<InstanceType<typeof TextField>>('firstField')

// A real, always-selected option ("No currency") rather than an absent value - Select
// shows its placeholder for '', not the option's own label.
const NONE = '__none__'

const advanced = ref(false)
const attempted = ref(false)
const kindId = ref<AccountKind['id']>(ACCOUNT_KINDS[0].id)
const group = ref(false)
const simpleName = ref('')
const codeAttempt = ref(0)
const MAX_CODE_ATTEMPTS = 20
const code = ref('')
const accountClass = ref<CreateAccountInput['accountClass']>('ASSET')
const type = ref<CreateAccountInput['type']>('OTHER')
const nameEn = ref('')
const nameHu = ref('')
const currency = ref<CurrencyCode | typeof NONE>(NONE)
const parentId = ref('')
const icon = ref<CreateAccountInput['icon']>(DEFAULT_ACCOUNT_ICON)
const color = ref(DEFAULT_ACCOUNT_COLOR)

const kind = computed(() => ACCOUNT_KINDS.find((k) => k.id === kindId.value)!)
const effectiveClass = computed(() => (advanced.value ? accountClass.value : kind.value.accountClass))
const choices = computed(() =>
  parentChoices(props.parentOptions, effectiveClass.value, props.defaultParentId),
)
const parent = computed(() => choices.value.find((option) => option.id === parentId.value))
const canBeGroup = computed(() => effectiveClass.value === 'ASSET' || effectiveClass.value === 'LIABILITY')
const isGroup = computed(() => group.value && canBeGroup.value)
const rule = computed(() => (isGroup.value ? 'none' : currencyRule(effectiveClass.value)))

function preferredParent(
  preferredCode: string | undefined,
  klass: CreateAccountInput['accountClass'],
): string {
  const list = parentChoices(props.parentOptions, klass, props.defaultParentId)
  return (
    list.find((option) => option.code === preferredCode)?.id ??
    classRootOf(list, klass)?.id ??
    list[0]?.id ??
    ''
  )
}

function reset(): void {
  const fromParent = props.parentOptions.find((option) => option.id === props.defaultParentId)
  const initialKind =
    ACCOUNT_KINDS.find((k) => k.accountClass === fromParent?.accountClass) ?? ACCOUNT_KINDS[0]
  advanced.value = false
  attempted.value = false
  kindId.value = initialKind.id
  group.value = false
  simpleName.value = ''
  codeAttempt.value = 0
  code.value = ''
  accountClass.value = initialKind.accountClass
  type.value = initialKind.type
  nameEn.value = ''
  nameHu.value = ''
  currency.value = NONE
  parentId.value = fromParent?.id ?? preferredParent(initialKind.parentCode, initialKind.accountClass)
  icon.value = initialKind.icon
  color.value = DEFAULT_ACCOUNT_COLOR
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    reset()
    void nextTick(() => firstFieldRef.value?.focus())
  },
)

const kindOptions = computed(() =>
  ACCOUNT_KINDS.map((k) => ({ value: k.id, label: t(`accounts.kinds.${k.id}`) })),
)
const accountClassOptions = ACCOUNT_CLASSES.map((value) => ({ value, label: t(`accounts.classes.${value}`) }))
const accountTypeOptions = computed(() =>
  LEGAL_TYPES[accountClass.value].map((value) => ({ value, label: t(`accounts.types.${value}`) })),
)
const accountIconOptions = computed(() =>
  ACCOUNT_ICONS.map((value) => ({ value, label: t(`accounts.iconNames.${value}`) })),
)
const currencyOptions = computed(() => [
  ...(rule.value === 'required' ? [] : [{ value: NONE, label: t('accounts.noCurrency') }]),
  ...Object.values(CurrencyCode).map((value) => ({ value, label: value })),
])
const parentIdOptions = computed(() =>
  choices.value.map((option) => ({
    value: option.id,
    label: `${'\u2003'.repeat(option.depth)}${option.breadcrumb}${option.currency ? ` · ${option.currency}` : ''}`,
  })),
)

const missingSimpleName = computed(() => attempted.value && simpleName.value.trim() === '')
const missingCode = computed(() => attempted.value && code.value.trim() === '')
const simpleNameError = computed(() => {
  if (missingSimpleName.value) return t('accounts.nameRequired')
  if (props.error?.field === 'code' && codeAttempt.value > MAX_CODE_ATTEMPTS) {
    return t('accounts.codeAlreadyUsed')
  }
  return undefined
})
const codeError = computed(() => {
  if (missingCode.value) return t('accounts.codeRequired')
  if (props.error?.field === 'code') return t('accounts.codeAlreadyUsed')
  return undefined
})
const missingNames = computed(
  () => attempted.value && (nameEn.value.trim() === '' || nameHu.value.trim() === ''),
)
const missingCurrency = computed(
  () => attempted.value && rule.value === 'required' && currency.value === NONE,
)
const missingParent = computed(() => attempted.value && parent.value === undefined)

function onKindChange(id: AccountKind['id']): void {
  kindId.value = id
  icon.value = kind.value.icon
  currency.value = NONE
  parentId.value = preferredParent(kind.value.parentCode, kind.value.accountClass)
}

function toggleAdvanced(): void {
  advanced.value = !advanced.value
  if (!advanced.value) return
  accountClass.value = kind.value.accountClass
  type.value = kind.value.type
}

function onClassChange(next: CreateAccountInput['accountClass']): void {
  accountClass.value = next
  type.value = 'OTHER'
  currency.value = NONE
  parentId.value = preferredParent(undefined, next)
}

function onSubmit(): void {
  attempted.value = true
  const chosen = parent.value
  if (chosen === undefined || missingCurrency.value) return
  const shared = {
    currency: rule.value === 'none' || currency.value === NONE ? undefined : currency.value,
    parentId: chosen.id,
    color: color.value,
  }
  if (advanced.value) {
    if (code.value.trim() === '') return
    if (nameEn.value.trim() === '' || nameHu.value.trim() === '') return
    const typed = code.value.trim()
    emit('submit', {
      ...shared,
      isGroup: isGroup.value,
      code: typed.startsWith(`${chosen.code}.`) ? typed : `${chosen.code}.${typed}`,
      accountClass: accountClass.value,
      type: type.value,
      names: { en: nameEn.value.trim(), hu: nameHu.value.trim() },
      icon: icon.value,
    })
    return
  }
  const name = simpleName.value.trim()
  if (name === '') return
  emit('submit', {
    ...shared,
    isGroup: isGroup.value,
    code: generateAccountCode(chosen.code, name, codeAttempt.value),
    accountClass: kind.value.accountClass,
    type: kind.value.type,
    names: { [locale.value === 'hu' ? 'hu' : 'en']: name },
    icon: kind.value.icon,
  })
}

watch(
  () => props.error,
  (error) => {
    if (error?.field !== 'code' || advanced.value) return
    codeAttempt.value += 1
    if (codeAttempt.value <= MAX_CODE_ATTEMPTS) onSubmit()
  },
)

const generalError = computed(() => props.error?.field === 'general')
const errorMessage = computed(() => {
  const code = props.error?.code
  if (code !== undefined && (FRIENDLY_CREATE_ERRORS as readonly string[]).includes(code)) {
    return t(`accounts.errors.${code.slice('account.'.length)}`)
  }
  return props.error?.detail ?? t('accounts.createErrorGeneral')
})
</script>

<template>
  <Dialog :open="open" :label="t('accounts.addAccount')" @close="emit('close')">
    <form
      class="flex max-h-[85vh] w-full flex-col gap-[var(--space-cell)] overflow-y-auto p-[var(--space-block)]"
      novalidate
      @submit.prevent="onSubmit"
    >
      <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
        {{ t('accounts.addAccount') }}
      </h2>

      <StatusTag v-if="generalError" tone="danger" role="alert">
        {{ errorMessage }}
      </StatusTag>

      <template v-if="!advanced">
        <label class="flex flex-col gap-[var(--space-hair)]">
          <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
            t('accounts.kind')
          }}</span>
          <Select
            :model-value="kindId"
            :options="kindOptions"
            :aria-label="t('accounts.kind')"
            @update:model-value="onKindChange($event as AccountKind['id'])"
          />
        </label>
        <TextField
          ref="firstField"
          v-model="simpleName"
          :label="t('accounts.names')"
          :maxlength="200"
          :error="simpleNameError"
        />
      </template>

      <template v-else>
        <TextField
          ref="firstField"
          v-model="code"
          :label="t('accounts.code')"
          :maxlength="64"
          :error="codeError"
        />

        <div class="grid grid-cols-2 gap-[var(--space-cell)]">
          <label class="flex flex-col gap-[var(--space-hair)]">
            <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
              t('accounts.accountClass')
            }}</span>
            <Select
              :model-value="accountClass"
              :options="accountClassOptions"
              @update:model-value="onClassChange"
            />
          </label>

          <label class="flex flex-col gap-[var(--space-hair)]">
            <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
              t('accounts.type')
            }}</span>
            <Select v-model="type" :options="accountTypeOptions" />
          </label>
        </div>

        <fieldset
          class="flex flex-col gap-[var(--space-inline)] rounded-[var(--radius-control)] border border-[color:var(--color-border)] p-[var(--space-cell)]"
        >
          <legend
            class="px-[var(--space-tight)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
          >
            {{ t('accounts.names') }}
          </legend>
          <TextField v-model="nameEn" :label="t('accounts.nameEn')" label-size="small" :maxlength="200" />
          <TextField v-model="nameHu" :label="t('accounts.nameHu')" label-size="small" :maxlength="200" />
          <StatusTag v-if="missingNames" tone="danger" role="alert">
            {{ t('accounts.missingLocaleError') }}
          </StatusTag>
          <StatusTag v-else-if="error?.field === 'names'" tone="danger" role="alert">
            {{ t('accounts.missingLocaleError') }}
          </StatusTag>
        </fieldset>
      </template>

      <label v-if="canBeGroup" class="flex items-start gap-[var(--space-inline)]">
        <input v-model="group" type="checkbox" class="mt-1" />
        <span class="flex flex-col">
          <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
            t('accounts.groupToggle')
          }}</span>
          <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
            t('accounts.groupHint')
          }}</span>
        </span>
      </label>

      <div class="grid grid-cols-2 gap-[var(--space-cell)]">
        <label v-if="rule !== 'none'" class="flex flex-col gap-[var(--space-hair)]">
          <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
            t('accounts.currency')
          }}</span>
          <Select
            v-model="currency"
            :options="currencyOptions"
            :placeholder="t('accounts.chooseCurrency')"
            :aria-label="t('accounts.currency')"
          />
          <StatusTag v-if="missingCurrency" tone="danger" role="alert">
            {{ t('accounts.currencyRequired') }}
          </StatusTag>
          <StatusTag v-else-if="error?.field === 'currency'" tone="danger" role="alert">
            {{ error.detail ?? t('accounts.createErrorGeneral') }}
          </StatusTag>
        </label>

        <label v-if="advanced" class="flex flex-col gap-[var(--space-hair)]">
          <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
            t('accounts.icon')
          }}</span>
          <div class="flex items-center gap-[var(--space-inline)]">
            <span
              class="flex h-[var(--density-control)] w-[var(--density-control)] shrink-0 items-center justify-center rounded-[var(--radius-control)]"
              :style="{ backgroundColor: `${color}22`, color }"
            >
              <AccountIcon :icon="icon" :label="t(`accounts.iconNames.${icon}`)" />
            </span>
            <Select v-model="icon" :options="accountIconOptions" />
          </div>
        </label>
      </div>

      <label class="flex flex-col gap-[var(--space-hair)]">
        <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
          t('accounts.parentAccount')
        }}</span>
        <Select v-model="parentId" :options="parentIdOptions" :aria-label="t('accounts.parentAccount')" />
        <StatusTag v-if="missingParent" tone="danger" role="alert">
          {{ t('accounts.parentRequired') }}
        </StatusTag>
      </label>

      <div v-if="advanced" class="flex flex-col gap-[var(--space-inline)]">
        <label class="flex items-center gap-[var(--space-inline)]">
          <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
            t('accounts.color')
          }}</span>
          <input
            v-model="color"
            type="color"
            :aria-label="t('accounts.color')"
            class="h-[var(--density-control)] w-12 rounded-[var(--radius-control)]"
          />
        </label>
        <div>
          <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
            {{ t('accounts.presetColors') }}
          </p>
          <div class="mt-[var(--space-hair)] flex flex-wrap gap-[var(--space-hair)]">
            <button
              v-for="preset in ACCOUNT_COLOR_PRESETS"
              :key="preset"
              type="button"
              :aria-label="preset"
              :aria-pressed="color === preset"
              class="h-6 w-6 rounded-full border transition-transform duration-[var(--motion-fast)]"
              :class="
                color === preset
                  ? 'scale-110 border-[color:var(--color-text)]'
                  : 'border-[color:var(--color-border)]'
              "
              :style="{ backgroundColor: preset }"
              @click="color = preset"
            />
          </div>
        </div>
      </div>

      <button
        type="button"
        :aria-expanded="advanced"
        class="self-start text-[color:var(--color-text-muted)] underline"
        @click="toggleAdvanced"
      >
        {{ advanced ? t('accounts.simpleMode') : t('accounts.advanced') }}
      </button>

      <div class="mt-[var(--space-hair)] flex justify-end gap-[var(--space-inline)]">
        <ActionButton variant="secondary" type="button" @click="emit('close')">
          {{ t('accounts.cancel') }}
        </ActionButton>
        <ActionButton type="submit" :disabled="isSubmitting">
          {{ isSubmitting ? t('accounts.creating') : t('accounts.create') }}
        </ActionButton>
      </div>
    </form>
  </Dialog>
</template>
